import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Loader2, Plus, Trash2, Globe, Building2, Users, Receipt, Upload } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';
import { FOREIGN_COUNTRIES, ENTITY_TYPES, JURISDICTIONS, JURISDICTION_LABEL, COUNTRY_NAMES } from '@/lib/foreignCatalog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { n: 1, label: 'Jurisdiction', icon: Globe },
  { n: 2, label: 'Company', icon: Building2 },
  { n: 3, label: 'Owners', icon: Users },
  { n: 4, label: 'Tax IDs', icon: Receipt },
  { n: 5, label: 'Review', icon: Check },
];

const emptyOwner = () => ({ owner_type: 'individual', full_name: '', ownership_percentage: '', roles: [], nationality: '', id_number: '', doc: null, uploading: false, uploadError: '' });
const emptyTaxId = () => ({ type: 'vat', jurisdiction: '', value: '' });

const OWNER_ROLES = ['Beneficial Owner', 'Shareholder', 'Director', 'Member', 'Partner', 'Nominee Director'];

const InternationalOnboardingPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [country, setCountry] = useState('');
  const [entityType, setEntityType] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [dateOfFormation, setDateOfFormation] = useState('');
  const [ein, setEin] = useState('');
  const [stateTaxId, setStateTaxId] = useState('');
  const [owners, setOwners] = useState([emptyOwner()]);
  const [taxIds, setTaxIds] = useState([]);

  const handleCountry = (c) => {
    setCountry(c);
    setEntityType('');
    const j = JURISDICTIONS[c] || [];
    setJurisdiction(j.length === 1 ? j[0] : '');
  };

  const next = () => {
    setError('');
    if (step === 1 && (!country || !entityType || !jurisdiction)) { setError('Select a country, entity type and jurisdiction.'); return; }
    if (step === 2 && !companyName.trim()) { setError('Company name is required.'); return; }
    if (step === 3) {
      for (let i = 0; i < owners.length; i++) {
        const o = owners[i];
        if (!o.full_name.trim() || !o.ownership_percentage || !o.nationality.trim() || !o.id_number.trim()) {
          setError(`Complete all fields for Owner ${i + 1} — every field is mandatory.`); return;
        }
        if (!o.roles || o.roles.length === 0) {
          setError(`Select at least one role for Owner ${i + 1}.`); return;
        }
        if (!o.doc) {
          setError(`Upload the ${o.owner_type === 'individual' ? 'passport' : 'formation certificate'} for Owner ${i + 1}.`); return;
        }
      }
    }
    setStep(s => Math.min(s + 1, 5));
  };
  const back = () => { setError(''); setStep(s => Math.max(s - 1, 1)); };

  const updateOwner = (i, field, val) => setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, [field]: val } : o));
  const toggleOwnerRole = (i, role) => setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, roles: o.roles.includes(role) ? o.roles.filter(r => r !== role) : [...o.roles, role] } : o));
  const removeOwner = (i) => setOwners(prev => prev.filter((_, idx) => idx !== i));
  const updateTaxId = (i, field, val) => setTaxIds(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: val } : t));
  const removeTaxId = (i) => setTaxIds(prev => prev.filter((_, idx) => idx !== i));

  const uploadOwnerDoc = async (i, file) => {
    if (!file) return;
    setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, uploading: true, uploadError: '' } : o));
    try {
      const pres = await axios.post(`${API}/upload/presigned`, { file_name: file.name, content_type: file.type || 'application/octet-stream' }, { headers: { Authorization: `Bearer ${token}` } });
      const { upload_url, file_url, s3_key } = pres.data;
      await axios.put(upload_url, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, uploading: false, doc: { file_name: file.name, file_url, s3_key } } : o));
    } catch (e) {
      setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, uploading: false, uploadError: 'Upload failed. Please try again.' } : o));
    }
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        company_name: companyName.trim(),
        country,
        jurisdiction,
        entity_type: entityType,
        date_of_formation: dateOfFormation || null,
        ein: ein.trim() || null,
        state_tax_id: stateTaxId.trim() || null,
        tax_identifiers: taxIds.filter(t => t.value.trim()).map(t => ({ type: t.type, value: t.value.trim(), jurisdiction: t.jurisdiction.trim() || null })),
        owners: owners.map(o => ({
          owner_type: o.owner_type,
          full_name: o.full_name.trim(),
          ownership_percentage: o.ownership_percentage ? parseFloat(o.ownership_percentage) : null,
          role: o.roles[0] || null,
          roles: o.roles,
          id_type: o.owner_type === 'individual' ? 'passport' : 'company_registration',
          id_number: o.id_number.trim() || null,
          nationality: o.nationality.trim() || null,
          document_type: o.owner_type === 'individual' ? 'passport' : 'formation_certificate',
          document_file_name: o.doc?.file_name || null,
          document_url: o.doc?.file_url || null,
          document_s3_key: o.doc?.s3_key || null,
        })),
      };
      const res = await axios.post(`${API}/companies/foreign`, payload, { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/dashboard/orders/international?company=${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create company. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#059669] focus:ring-1 focus:ring-[#059669]';

  return (
    <AppLayout>
      <Link to="/dashboard/companies" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Companies
      </Link>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Set up a Company</h1>
        <p className="text-gray-400 text-sm mt-1">Onboard a company in the US, UK, Canada, Singapore or the UAE.</p>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="onboarding-steps">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = step === s.n;
          const done = step > s.n;
          return (
            <React.Fragment key={s.n}>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap ${active ? 'bg-[#059669]/15 text-[#059669]' : done ? 'text-[#059669]' : 'text-gray-500'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${active ? 'bg-[#059669] text-black' : done ? 'bg-[#059669]/30' : 'bg-gray-800'}`}>
                  {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                </div>
                <span className="text-sm font-medium">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="w-6 h-px bg-gray-700" />}
            </React.Fragment>
          );
        })}
      </div>

      {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6"><p className="text-red-400 text-sm">{error}</p></div>}

      <div className="bg-[#111827] border border-gray-800 rounded-lg p-6 max-w-3xl">
        {/* Step 1: Jurisdiction */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm text-gray-400 mb-3">Country</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <button key="US" onClick={() => navigate('/dashboard/companies/new')}
                  className="p-4 rounded-lg border-2 text-left transition-all border-gray-700 hover:border-[#059669] hover:bg-[#059669]/5"
                  data-testid="country-US">
                  <img src="https://flagcdn.com/48x36/us.png" alt="United States flag" className="w-10 h-[30px] rounded object-cover mb-3 shadow" />
                  <p className="text-white text-sm font-semibold">United States</p>
                  <p className="text-gray-500 text-xs mt-0.5">US</p>
                </button>
                {FOREIGN_COUNTRIES.map(c => {
                  const iso = { UK: 'gb', CA: 'ca', SG: 'sg', UAE: 'ae' }[c.code] || 'un';
                  const active = country === c.code;
                  return (
                    <button key={c.code} onClick={() => handleCountry(c.code)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${active ? 'border-[#059669] bg-[#059669]/10' : 'border-gray-700 hover:border-[#059669] hover:bg-[#059669]/5'}`}
                      data-testid={`country-${c.code}`}>
                      <img src={`https://flagcdn.com/48x36/${iso}.png`} alt={`${c.name} flag`} className="w-10 h-[30px] rounded object-cover mb-3 shadow" />
                      <p className="text-white text-sm font-semibold">{c.name}</p>
                      <p className={`text-xs mt-0.5 ${active ? 'text-[#059669]' : 'text-gray-500'}`}>{c.code}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            {country && (
              <>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Entity Type</label>
                  <select value={entityType} onChange={e => setEntityType(e.target.value)} className={inputCls} data-testid="entity-type-select">
                    <option value="">Select entity type</option>
                    {(ENTITY_TYPES[country] || []).map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">{JURISDICTION_LABEL[country] || 'Jurisdiction'}</label>
                  <select value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} className={inputCls} data-testid="jurisdiction-select">
                    <option value="">Select {JURISDICTION_LABEL[country] || 'jurisdiction'}</option>
                    {(JURISDICTIONS[country] || []).map(j => <option key={j} value={j}>{j}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 2: Company */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Name</label>
              <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="e.g. Falcon Trading Ltd" className={inputCls} data-testid="company-name-input" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Date of Formation <span className="text-gray-600">(optional)</span></label>
              <input type="date" value={dateOfFormation} onChange={e => setDateOfFormation(e.target.value)} className={inputCls} data-testid="formation-date-input" />
            </div>
            <div className="text-sm text-gray-500">
              Jurisdiction: <span className="text-gray-300">{COUNTRY_NAMES[country]} — {jurisdiction}</span> · Entity: <span className="text-gray-300">{entityType}</span>
            </div>
          </div>
        )}

        {/* Step 3: Owners */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">Add every beneficial owner. All fields are mandatory. Individuals must upload a passport; corporate owners must upload a formation certificate.</p>
            {owners.map((o, i) => {
              const isCompany = o.owner_type === 'company';
              return (
                <div key={i} className="border border-gray-700 rounded-lg p-4" data-testid={`owner-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-400 text-sm">Owner {i + 1}</span>
                    {owners.length > 1 && <button onClick={() => removeOwner(i)} className="text-red-400 hover:text-red-300" data-testid={`owner-remove-${i}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>

                  {/* Owner type toggle */}
                  <div className="flex gap-2 mb-4">
                    {['individual', 'company'].map(t => (
                      <button key={t} onClick={() => updateOwner(i, 'owner_type', t)}
                        className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium capitalize transition-all ${o.owner_type === t ? 'border-[#059669] bg-[#059669]/10 text-[#059669]' : 'border-gray-700 text-gray-300 hover:border-gray-600'}`}
                        data-testid={`owner-type-${t}-${i}`}>
                        {t === 'individual' ? 'Individual' : 'Owned by a Company'}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input type="text" value={o.full_name} onChange={e => updateOwner(i, 'full_name', e.target.value)} placeholder={isCompany ? 'Company / entity name *' : 'Full name *'} className={inputCls} data-testid={`owner-name-${i}`} />
                    <input type="number" value={o.ownership_percentage} onChange={e => updateOwner(i, 'ownership_percentage', e.target.value)} placeholder="Ownership % *" className={inputCls} data-testid={`owner-pct-${i}`} />
                    <input type="text" value={o.nationality} onChange={e => updateOwner(i, 'nationality', e.target.value)} placeholder={isCompany ? 'Country of incorporation *' : 'Nationality *'} className={inputCls} data-testid={`owner-nationality-${i}`} />
                    <input type="text" value={o.id_number} onChange={e => updateOwner(i, 'id_number', e.target.value)} placeholder={isCompany ? 'Company registration no. *' : 'Passport number *'} className={inputCls} data-testid={`owner-idnum-${i}`} />
                  </div>

                  {/* Roles — multi-select (a person can hold several) */}
                  <div className="mt-3">
                    <label className="block text-sm text-gray-400 mb-2">Role(s) <span className="text-red-400">*</span> <span className="text-gray-600 text-xs">— select all that apply</span></label>
                    <div className="flex flex-wrap gap-2">
                      {OWNER_ROLES.map(r => {
                        const on = o.roles.includes(r);
                        return (
                          <button key={r} type="button" onClick={() => toggleOwnerRole(i, r)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${on ? 'border-[#059669] bg-[#059669]/15 text-[#059669]' : 'border-gray-700 text-gray-300 hover:border-gray-600'}`}
                            data-testid={`owner-role-${r.replace(/\s+/g, '-').toLowerCase()}-${i}`}>
                            {on && <Check className="w-3 h-3 inline mr-1" />}{r}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Mandatory document upload */}
                  <div className="mt-3">
                    <label className="block text-sm text-gray-400 mb-2">{isCompany ? 'Formation Certificate' : 'Passport'} <span className="text-red-400">*</span></label>
                    {o.doc ? (
                      <div className="flex items-center justify-between bg-[#059669]/10 border border-[#059669]/30 rounded-lg px-3 py-2">
                        <span className="text-[#059669] text-sm truncate flex items-center gap-2"><Check className="w-4 h-4" /> {o.doc.file_name}</span>
                        <button onClick={() => updateOwner(i, 'doc', null)} className="text-gray-400 hover:text-red-400 text-xs">Replace</button>
                      </div>
                    ) : (
                      <label className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg py-3 cursor-pointer transition-colors ${o.uploading ? 'border-gray-700 text-gray-500' : 'border-gray-700 hover:border-[#059669] text-gray-300'}`} data-testid={`owner-upload-label-${i}`}>
                        {o.uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> Upload {isCompany ? 'formation certificate' : 'passport'}</>}
                        <input type="file" accept="image/*,application/pdf" className="hidden" disabled={o.uploading} onChange={e => uploadOwnerDoc(i, e.target.files[0])} data-testid={`owner-upload-${i}`} />
                      </label>
                    )}
                    {o.uploadError && <p className="text-red-400 text-xs mt-1">{o.uploadError}</p>}
                  </div>
                </div>
              );
            })}
            <button onClick={() => setOwners([...owners, emptyOwner()])} className="inline-flex items-center gap-2 text-[#059669] hover:text-[#00D46E] text-sm" data-testid="add-owner-btn">
              <Plus className="w-4 h-4" /> Add another owner
            </button>
          </div>
        )}

        {/* Step 4: Tax IDs */}
        {step === 4 && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Federal Tax ID (EIN) <span className="text-gray-600">(optional)</span></label>
                <input type="text" value={ein} onChange={e => setEin(e.target.value)} placeholder="If already assigned" className={inputCls} data-testid="ein-input" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">State Tax ID <span className="text-gray-600">(optional)</span></label>
                <input type="text" value={stateTaxId} onChange={e => setStateTaxId(e.target.value)} placeholder="State/provincial tax no." className={inputCls} data-testid="state-tax-id-input" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-gray-400">Sales Tax / VAT / GST Numbers <span className="text-gray-600">(optional)</span></label>
                <button onClick={() => setTaxIds([...taxIds, emptyTaxId()])} className="inline-flex items-center gap-1 text-[#059669] hover:text-[#00D46E] text-sm" data-testid="add-tax-id-btn"><Plus className="w-4 h-4" /> Add</button>
              </div>
              {taxIds.length === 0 ? <p className="text-gray-500 text-sm">None added</p> : (
                <div className="space-y-2">
                  {taxIds.map((t, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`tax-id-row-${i}`}>
                      <select value={t.type} onChange={e => updateTaxId(i, 'type', e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white">
                        <option value="sales_tax">Sales Tax</option>
                        <option value="vat">VAT</option>
                        <option value="gst">GST</option>
                        <option value="other">Other</option>
                      </select>
                      <input type="text" value={t.jurisdiction} onChange={e => updateTaxId(i, 'jurisdiction', e.target.value)} placeholder="Jurisdiction" className="bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white w-32" />
                      <input type="text" value={t.value} onChange={e => updateTaxId(i, 'value', e.target.value)} placeholder="Number" className="bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white w-48 font-mono" />
                      <button onClick={() => removeTaxId(i)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Review */}
        {step === 5 && (
          <div className="space-y-4" data-testid="review-section">
            <Row label="Country" value={`${COUNTRY_NAMES[country]} (${country})`} />
            <Row label="Entity Type" value={entityType} />
            <Row label={JURISDICTION_LABEL[country] || 'Jurisdiction'} value={jurisdiction} />
            <Row label="Company Name" value={companyName} />
            {dateOfFormation && <Row label="Date of Formation" value={dateOfFormation} />}
            <div className="pt-2 border-t border-gray-800">
              <p className="text-gray-400 text-sm mb-2">Owners ({owners.length})</p>
              {owners.map((o, i) => (
                <p key={i} className="text-white text-sm">
                  {o.full_name} — {(o.roles || []).join(', ') || '—'} ({o.owner_type === 'individual' ? 'Individual' : 'Company'}){o.ownership_percentage ? ` · ${o.ownership_percentage}%` : ''}
                  {o.doc && <span className="text-[#059669] text-xs ml-2">✓ {o.owner_type === 'individual' ? 'Passport' : 'Certificate'}</span>}
                </p>
              ))}
            </div>
            {(ein || stateTaxId || taxIds.filter(t => t.value.trim()).length > 0) && (
              <div className="pt-2 border-t border-gray-800">
                <p className="text-gray-400 text-sm mb-2">Tax Identifiers</p>
                {ein && <p className="text-white text-sm">Federal Tax ID: <span className="font-mono">{ein}</span></p>}
                {stateTaxId && <p className="text-white text-sm">State Tax ID: <span className="font-mono">{stateTaxId}</span></p>}
                {taxIds.filter(t => t.value.trim()).map((t, i) => (
                  <p key={i} className="text-white text-sm"><span className="uppercase text-gray-500 text-xs mr-1">{t.type}</span>{t.jurisdiction ? `${t.jurisdiction}: ` : ''}<span className="font-mono">{t.value}</span></p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Nav */}
        <div className="flex justify-between mt-8">
          <button onClick={back} disabled={step === 1} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-40" data-testid="onboarding-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < 5 ? (
            <button onClick={next} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-white" style={{ backgroundColor: '#059669' }} data-testid="onboarding-next-btn">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={saving} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#059669' }} data-testid="onboarding-submit-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><Check className="w-4 h-4" /> Create Company</>}
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-400">{label}</span>
    <span className="text-white text-right">{value}</span>
  </div>
);

export default InternationalOnboardingPage;
